import React, { useState, useEffect, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { Tooltip, IconButton, TextareaAutosize, CircularProgress } from '@mui/material';
import FormDialog from '../../../../components/Dialog/FormDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { putPlannerTodos } from '../../../../sevices/apiPlannerTodos';
import toast from 'react-hot-toast';

import NoteIcon from '../../../../styles/svg/planner-notes.svg';
import AddPlannerNotes from '../../../../styles/svg/add-planner-notes.svg';
import stickyImage from "../../../../styles/svg/sticky-icon.svg";
import DueDate from './DueDate';
import { useCard } from '../../../../utils/reactQuery/cards';
import BoardCardKey from '../../../../components/Kanban/KanbanCard/BoardCardKey';
import { useSetMultipleParams } from '../../../../hooks/useSetMultipleParams';
import NotePopover from '../components/NotePopover';

import mediumPriorityIcon from "../../../../styles/svg/medium_priority.svg";
import highPriorityIcon from "../../../../styles/svg/high_priority.svg";
import lowPriorityIcon from "../../../../styles/svg/low_priority.svg";
import criticalPriorityIcon from "../../../../styles/svg/critical_priority.svg";
import deleteIcon from "../../../../styles/svg/adminWorkflowDelete.svg";

import { formatTimeFromNow } from '../../../../utils';
import AssignCard from '../../../board/components/Kanban/AssignCard';
import AssigneUser from './AssigneUser';
import DynamicColorNote from '../components/DynamicColorNote';
import { newFormatDate } from '../../../../utils/helpers';

const getPriorityIcon = (priority) => {
  const priorityIcons = {
    Critical: criticalPriorityIcon,
    High: highPriorityIcon,
    Medium: mediumPriorityIcon,
    Low: lowPriorityIcon
  };
  return priorityIcons[priority] || "";
};

/**
 * Component to render an individual task item in the planner list
 */
const MyPlannerListItem = ({
  task,
  index,
  handleDeleteTask,
  handleToggleTask,
  handleOpenNotes,
  updateDueDate,
  isDeleteEnabled = true,
  setOpenDetailCard,
  filter,
}) => {
  const [setMultipleSearchParams] = useSetMultipleParams();
  const { isLoading, error, card, isError } = useCard(task.source_id);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChecked, setIsChecked] = useState(Boolean(task.completed_at));
  const queryClient = useQueryClient();
  
  // Local state for task editing
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(task.title);
  const editInputRef = useRef(null);
  
  // State for note preview popover
  const [noteAnchorEl, setNoteAnchorEl] = useState(null);
  const openNotePreview = Boolean(noteAnchorEl);

  // Update mutation
  const { mutate: updateTask } = useMutation({
    mutationFn: putPlannerTodos,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      toast.success('Task updated successfully');
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to update task');
    }
  });

  useEffect(() => {
    setIsChecked(Boolean(task.completed_at));
  }, [task.completed_at]);

  // Set the initial title value when task changes
  useEffect(() => {
    setEditedTitle(task.title);
  }, [task.title]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && editInputRef.current) {
      // Move cursor to the end of text
      editInputRef.current.focus();
      const length = task.title.length;
      editInputRef.current.setSelectionRange(length, length);
    }
  }, [isEditing]);

  const cardData = card?.data.data.responseData;
  const { allocated_hours = null, total_hours_spent = null } = cardData || {};

  // Handle start editing mode
  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedTitle(task.title);
  };

  // Handle save edit
  const handleSaveEdit = () => {
    if (editedTitle.trim() !== '' && editedTitle.trim() !== task.title) {
      updateTask({
        id: task.id,
        isMove: false,
        data: {
          title: editedTitle.trim()
        }
      });
    }
    setIsEditing(false);
  };

  // Handle keyboard events in edit mode
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditedTitle(task.title); // Reset to original title
    }
  };

  const handleDetails = (event) => {
    // Check if modifier keys are pressed (Cmd/Ctrl or Shift)
    const isModifierPressed = event.metaKey || event.ctrlKey || event.shiftKey;
    
    if (isModifierPressed) {
      // For new tab/window, construct the URL and open it
      const params = new URLSearchParams();
      params.set("card_id", task.source_id);
      params.set("brd_id", card?.data.data.responseData.board_id);
      window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
      event.stopPropagation();
      return;
    }
    
    setMultipleSearchParams([
      { name: "card_id", value: task.source_id },
      { name: "brd_id", value: card?.data.data.responseData.board_id },
    ]);

    setOpenDetailCard(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleting(true);

    // Call the handleDeleteTask function which now returns a promise
    handleDeleteTask(task.id)
      .then(() => {
        // Only close the dialog after successful deletion
        setShowDeleteConfirmation(false);
        setIsDeleting(false);
      })
      .catch((error) => {
        console.error("Error deleting task:", error);
        setIsDeleting(false);
        // You might want to show an error message here
      });
  };

  // Handlers for note preview popover
  const handleNoteMouseEnter = (event) => {
    if (task?.notes?.length > 0) {
      setNoteAnchorEl(event.currentTarget);
    }
  };

  const handleNoteMouseLeave = () => {
    // We'll use a short timeout to allow the cursor to move to the popover
    // This prevents flickering when moving from the icon to the popover
    setTimeout(() => {
      // Only close if we've really left (not if we've moved to the popover)
      // The popover component will handle its own mouse events
      setNoteAnchorEl(null);
    }, 100);
  };

  return (
    <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
      {(provided) => (
        <div className="planner-task-item-container">
          <div
            className={`task-item draggable-item  ${
              task?.completed_at && filter !== "Completed"
                ? "task-completed"
                : ""
            }`}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            {isDeleteEnabled && (
              <>
                {task?.source_id ? (
                  <CloseIcon
                    className="task-action close"
                    onClick={() => setShowDeleteConfirmation(true)}
                  />
                ) : (
                  <img
                    src={deleteIcon}
                    alt="delete"
                    className="task-action close delete-icon-task"
                    onClick={() => setShowDeleteConfirmation(true)}
                  />
                )}
              </>
            )}
            {/* Delete Confirmation Dialog */}
            {isDeleteEnabled && (
              <FormDialog
                open={showDeleteConfirmation}
                handleClose={() =>
                  !isDeleting && setShowDeleteConfirmation(false)
                }
                handleSubmit={handleConfirmDelete}
                title={
                  task?.source_id
                    ? "Confirmation: Remove Task "
                    : "Confirmation: Delete Todo"
                }
                buttonTxt={task?.source_id ? "Remove" : "Delete"}
                cancelBtn={true}
                btnColor="#f44336"
                width="400px"
                disabled={isDeleting}
                isLoading={isDeleting}
                className="delete-task-confirmation-dialog"
              >
                <div style={{ textAlign: "center" }}>
                  <>
                    <p style={{ fontWeight: "bold", marginBottom: "10px" }}>
                      {task?.source_id
                        ? "Do you want to remove this task from your todo?"
                        : "Do you want to delete this todo?"}
                    </p>
                    <p>
                      This action cannot be undone, and any notes associated
                      with this task will also be permanently deleted.
                    </p>
                  </>
                </div>
              </FormDialog>
            )}

            <div className="task-drag-handle"></div>
            <div className="task-checkbox" style={{ pointerEvents: "auto" }}>
              <label
                className="custom-checkbox"
                style={{ pointerEvents: "auto" }}
              >
                <input
                  type="checkbox"
                  id={`board-list-my-task-checkbox-all-board`}
                  checked={isChecked}
                  onChange={(e) => {
                    handleToggleTask(task.id, e);
                    setIsChecked(e.target.checked);
                  }}
                  style={{ pointerEvents: "auto" }}
                />
                <span
                  className="checkmark"
                  style={{ pointerEvents: "auto" }}
                ></span>
              </label>
            </div>
            <div className="task-content">
              {task?.source_id && card && (
                <>
                  <div className="d-flex align-items-center">
                    <AssigneUser card={card?.data.data.responseData} />
 
                    <div className="task-header w-100">
                      <div className="ml-2">
                        <p className="d-flex align-items-center">
                          <Tooltip
                            title={`priority: ${card?.data.data.responseData?.priority}`}
                          >
                            <img
                              className="priority_icon cursor-pointer"
                              src={getPriorityIcon(
                                card?.data.data.responseData?.priority
                              )}
                              alt="priority"
                            />
                          </Tooltip>
                          <BoardCardKey
                            project={card?.data.data.responseData}
                            boardKey={card?.data.data.responseData.board.key}
                            handleDetails={handleDetails}
                          />
                        </p>
 
                        <p className={`related_time`}>
                          {formatTimeFromNow(
                            card?.data.data.responseData.date_created
                          )}
                        </p>
                      </div>
                      <p className="todo-workstep-status">{card?.data?.data?.responseData?.workflow_step_status}</p>
 
                      {task?.source_id && (
                        <div className="task-completion">
                          {(
                            (allocated_hours &&
                              total_hours_spent) ?
                              <p className="complete-percent">
                                {total_hours_spent}/{allocated_hours}{" "}
                                <span className="">|</span>{" "}
                                {allocated_hours && total_hours_spent
                                  ? Math.round(
                                    (total_hours_spent / allocated_hours) * 100
                                  )
                                  : 0}
                                % completed
                              </p> : '')}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              {isEditing ? (
                <TextareaAutosize
                  ref={editInputRef}
                  className="task-text-edit"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={handleSaveEdit}
                  onKeyDown={handleEditKeyDown}
                  minRows={1}
                  maxRows={5}
                  autoFocus
                  spellCheck={false}
                />
              ) : (
                <p className="task-text" onClick={handleStartEditing}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: task.title.replace(/\n/g, "<br/>"),
                    }}
                  />
                </p>
              )}
              {/* Level and Milestone UI */}
              {task?.source_id && card && (
                <div className="d-flex flex-wrap mt-1">
                  {/* Display Labels (Levels) */}
                  {card?.data?.data?.responseData?.labels &&
                    card.data.data.responseData.labels.length > 0 &&
                    card.data.data.responseData.labels[0] !== "" && (
                      <div className="d-flex flex-wrap">
                        {card.data.data.responseData.labels.map(
                          (label, index) => (
                            <div
                              key={`${label.label_id}-${index}`}
                              className="card-level-tag mr-1"
                            >
                              <p className="card-level-tag-text">
                                {label.label}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    )}

                  {/* Display Epic (Milestone) */}
                  {card?.data?.data?.responseData?.epic && (
                    <div className="d-flex">
                      <div className="card-epic-tag mr-1">
                        <p className="card-epic-tag-text">
                          {card.data.data.responseData.epic.epic_name}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="task-actions">
              <div onMouseLeave={handleNoteMouseLeave}>
                <IconButton
                  className="task-action notes"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenNotes(task.id);
                  }}
                  onMouseEnter={handleNoteMouseEnter}
                >
                  {task?.notes?.length > 0 ? (
                    <DynamicColorNote fill={task?.notes[0]?.color_code} />
                  ) : (
                    <img src={AddPlannerNotes} alt="notes" />
                  )}
                </IconButton>

                {/* Note Preview using the new NotePopover component */}
                <NotePopover
                  open={openNotePreview}
                  anchorEl={noteAnchorEl}
                  onClose={handleNoteMouseLeave}
                  title={task?.notes?.length > 0 ? task.notes[0]?.title : ""}
                  content={
                    task?.notes?.length > 0 ? task.notes[0]?.content : ""
                  }
                  date={
                    task?.notes?.length > 0
                      ? newFormatDate(task.notes[0]?.created_at)
                      : ""
                  }
                  backgroundColor={
                    task?.notes?.length > 0
                      ? task.notes[0]?.color_code || "#fff9c4"
                      : "#fff9c4"
                  }
                />
              </div>
              <DueDate
                dueDate={task?.due_date}
                taskId={task.id}
                onDateChange={updateDueDate}
              />
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default MyPlannerListItem; 